import { ListFilter } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useOrderFormStore } from '@/modules/order/stores/order-form.store'

import { searchFilterConstant } from '../constants/search-filter.constant'
import type { PayloadSearch } from '../types/payload-search.type'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Form, FormControl, FormField, FormItem } from './ui/form'
import { Label } from './ui/label'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import { Search } from './ui/search'

type FormSearchInputComponentProps = {
	formSearchInputController: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		filter: any[]
		iconOnly?: boolean
		filterDefault: string
		placeholder: string
		isDisabled?: boolean
		searchType?: 'client' | 'transport' | 'product'
		onSubmitRequest: ({ payload }: PayloadSearch) => void
	}
}

export function FormSearchInputComponent({
	formSearchInputController,
}: FormSearchInputComponentProps) {
	const [selectedFilter, setSelectedFilter] = useState(
		formSearchInputController.filterDefault ?? '',
	)
	const [timeoutId, setTimeoutId] = useState(null)
	const [isOpen, setIsOpen] = useState(false)

	const { jsonOrderForm } = useOrderFormStore()

	const form = useForm({
		defaultValues: {
			inputValue: '',

		},
	})
	const handleSubmitRequest = (data: {
		inputValue: string
		inputValueDate: string
	}) => {
		if (formSearchInputController.isDisabled) return

		const payload = {
			inputValue: data.inputValue,
			filter: selectedFilter,
			inputValueDate: data.inputValueDate ?? '',
		}

		formSearchInputController.onSubmitRequest({ payload })
	}

	const handleChangeFilter = (filter: string) => {
		form.setValue('inputValue', '')
		setSelectedFilter(filter)
		setIsOpen(false)
	}

	const searchTypeToFieldValue = {
		client: jsonOrderForm?.cliente?.razaoSocialNome,
		transport: jsonOrderForm?.redespacho?.razaoSocialNome,
	}

	useEffect(() => {
		if (
			formSearchInputController.searchType === 'client' ||
			formSearchInputController.searchType === 'transport'
		) {
			form.setValue(
				'inputValue',
				searchTypeToFieldValue[formSearchInputController.searchType],
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jsonOrderForm])

	return (
		<div className="flex gap-2 flex-1">
			<Button
				disabled={formSearchInputController.isDisabled}
				onClick={() => setIsOpen(true)}
				variant="secondary"
			>
				<ListFilter size={16} />
			</Button>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmitRequest)}
					className="flex-1 md:flex-none md:w-96"
				>
					<FormField
						control={form.control}
						name="inputValue"
						render={({ field }) => (
							<FormItem className="flex flex-1 w-full">
								<FormControl>
									<Search
										{...field}
										onClick={(e) => {
											const inputElement = e.target as HTMLInputElement
											inputElement.select()
										}}
										onChange={(e) => {
											const value = e.currentTarget.value
											field.onChange(value)
											clearTimeout(timeoutId)

											const newTimeoutId = setTimeout(() => {
												if (
													formSearchInputController.searchType === 'product'
												) {
													form.handleSubmit(handleSubmitRequest)()
												}
											}, 350)

											setTimeoutId(newTimeoutId)
										}}
										disabled={formSearchInputController.isDisabled}
										onClickSearchIcon={form.handleSubmit(handleSubmitRequest)}
										placeholder={`${formSearchInputController.placeholder} - ${searchFilterConstant[selectedFilter]}`}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</form>
			</Form>

			<Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Filtros</DialogTitle>
					</DialogHeader>
					<RadioGroup value={selectedFilter} className="space-y-3">
						{formSearchInputController.filter.map((item) => (
							<div key={item.id} className="flex items-center space-x-2 bg-red-400">
								<RadioGroupItem
									onClick={() => handleChangeFilter(item.parameter)}
									key={item.id}
									id={item.id.toString()}
									value={item.parameter}
								/>
								<Label htmlFor={item.id.toString()} className="text-md">
									{item.text}
								</Label>
							</div>
						))}
					</RadioGroup>
				</DialogContent>
			</Dialog>
		</div>
	)
}
